import { useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import LoadingSpinner from '../../components/LoadingSpinner';
import { GeneratedPDF, Service } from '../../core/_models';
import usePageTitle from '../../hooks/usePageTitle';
import {
    ColumnDef,
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { useGeneratedPDFs } from '../../hooks/useGeneratedPDFs';
import { usePageHeader } from '../../contexts/PageHeaderContext';

export function GeneratedPDFsList() {
    usePageTitle('View Generated PDFs');
    const { setPageHeader } = usePageHeader();

    const {
        generatedPDFs: items,
        totalGeneratedPDFs: total,
        loading,
        loadingError,
        reload,
        loadMore,
        hasMore,
        loadingMore,
    } = useGeneratedPDFs();

    // Set up page header
    useEffect(() => {
        const title = total !== null ? `Generated PDFs (${total} total)` : 'Generated PDFs';
        setPageHeader(title);
        return () => setPageHeader(null);
    }, [setPageHeader, total]);

    const data = useMemo(() => items, [items]);

    const columns = useMemo<ColumnDef<GeneratedPDF>[]>(
        () => [
            {
                accessorKey: 'service',
                header: 'Deceased Name',
                cell: ({ getValue }) => {
                    const service = getValue() as Service | null | undefined;
                    if (!service) {
                        return <span className="text-gray-500">No service data</span>;
                    }
                    const uniqueIdentifier = service.unique_identifier;
                    const deceasedName =
                        service.public_data?.deceased_person?.full_name ?? '';
                    return (
                        <Link
                            className="text-blue-600 hover:underline"
                            to={`/services/${uniqueIdentifier}/send-service-to-family`}
                        >
                            {deceasedName}
                        </Link>
                    );
                },
                sortingFn: (a, b, id) => {
                    const getDeceasedName = (v: any) =>
                        v && typeof v === 'object' && 'public_data' in v
                            ? v.public_data?.deceased_person?.full_name ?? ''
                            : '';
                    return getDeceasedName(a.getValue(id)).localeCompare(
                        getDeceasedName(b.getValue(id)),
                    );
                },
            },
            {
                accessorKey: 'service',
                header: 'Account',
                cell: ({ getValue }) => {
                    const service = getValue() as Service | null | undefined;
                    const account = service?.account;
                    if (!account) return '';
                    return typeof account === 'string' ? account : account.name;
                },
                sortingFn: (a, b, id) => {
                    const getAccountName = (v: any) => {
                        if (v && typeof v === 'object' && 'account' in v) {
                            const account = v.account;
                            return typeof account === 'string'
                                ? account
                                : account?.name ?? '';
                        }
                        return '';
                    };
                    return getAccountName(a.getValue(id)).localeCompare(
                        getAccountName(b.getValue(id)),
                    );
                },
            },
            {
                accessorKey: 'inserted_at',
                header: 'Created',
                cell: ({ getValue }) =>
                    DateTime.fromISO(getValue() as string).toRelative(),
            },
        ],
        [],
    );

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div
                    className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
                    role="alert"
                >
                    <span className="block sm:inline">{loadingError}</span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={reload}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fixed">
            <div className="overflow-x-auto">
                <table className="table table-fixed table-border">
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className="px-4 py-2 bg-gray-100 cursor-pointer whitespace-nowrap"
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                        <span>
                                            {header.column.getIsSorted() === 'asc'
                                                ? ' 🔼'
                                                : header.column.getIsSorted() ===
                                                    'desc'
                                                    ? ' 🔽'
                                                    : ''}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length > 0 ? (
                            table.getRowModel().rows.map((row) => (
                                <tr key={row.id} className="border-t">
                                    {row.getVisibleCells().map((cell) => (
                                        <td key={cell.id} className="px-4 py-2">
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className="text-center py-4"
                                >
                                    No generated PDFs have been created.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Load More */}
            {hasMore && (
                <div className="text-center mt-4">
                    <button
                        className="btn btn-light"
                        onClick={loadMore}
                        disabled={loadingMore}
                    >
                        {loadingMore ? 'Loading…' : 'Load More'}
                    </button>
                </div>
            )}
        </div>
    );
}